import { useState } from "react"

import * as Sentry from "@sentry/react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { ShiftDetails } from "@/components/sheriff/shift/ShiftDetailsDialog"
import { useAuth } from "@/contexts/AuthContext"
import { withCancellationAuditComment, withUpdateAuditComment } from "@/hooks/useShiftAuditComment"
import { getErrorMessage } from "@/lib/getErrorMessage"
import { buildShiftSaveDiff } from "@/lib/shiftSaveDiff"
import { supabase } from "@/lib/supabase"
import { ukCalendarDate } from "@/lib/ukCalendarDate"
import {
  CancelShiftBody,
  type CancelledReason,
  type Shift,
  shiftsService,
} from "@/services/shiftsService"
import { timesheetService } from "@/services/timesheetService"
import { type ShiftDisplayStatus } from "@/utils/shiftDisplayStatus"
import { formatTo24HourTime } from "@/utils/timeFormat"

// Computed display properties - added to Shift, not replacing anything
export type ShiftWithDisplay = Shift & {
  displayStatus: ShiftDisplayStatus
}

export function useShiftDialog() {
  const [shiftDetailsOpen, setShiftDetailsOpen] = useState(false)
  const [selectedShift, setSelectedShift] = useState<ShiftDetails | null>(null)
  const [detailsMode, setDetailsMode] = useState<"view" | "edit">("view")
  const [isSaving, setIsSaving] = useState(false)
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const isDSP = user?.role === "DSP"
  const { logger } = Sentry
  const sentryVerbose = import.meta.env.VITE_SENTRY_VERBOSE_UI === "true"

  const handleShiftClick = (shift: ShiftWithDisplay) => {
    Sentry.addBreadcrumb({
      category: "ui",
      level: "info",
      message: "Sheriff shift selected",
      data: { shiftId: shift.id, status: shift.displayStatus },
    })

    if (sentryVerbose) {
      Sentry.startSpan(
        {
          op: "ui.click",
          name: "Sheriff Shift Click",
        },
        (span) => {
          span.setAttribute("shift_id", shift.id)
          span.setAttribute("display_status", shift.displayStatus)
        },
      )
    }

    const shiftDetails: ShiftDetails = {
      id: shift.id,
      name: shift.people?.person || "Unassigned",
      personId: shift.people?.person || undefined,
      timesheetId: shift.timesheet?.id || undefined,
      startDate: ukCalendarDate(new Date(shift.startTime)),
      endDate: ukCalendarDate(new Date(shift.endTime)),
      startTime: formatTo24HourTime(shift.startTime)!,
      endTime: formatTo24HourTime(shift.endTime)!,
      realStartTime: formatTo24HourTime(shift.timesheet?.clockedInTime),
      realEndTime: formatTo24HourTime(shift.timesheet?.clockedOutTime),
      location: shift.location,
      role: shift.employer,
      notes: shift.notes || undefined,
      status: shift.displayStatus as ShiftDetails["status"],
      vehicleId: shift.vehicle?.id ?? null,
      vehicleReg: shift.vehicle?.reg ?? null,
      lastEditedBy: shift.lastEditedBy || undefined,
      lastEditedAt: undefined,
      // Add filter fields for PersonCombobox
      area: shift.area,
      type: shift.type,
      employer: shift.employer,
      cancelledReason: shift.cancelledReason as CancelledReason,
    }

    setSelectedShift(shiftDetails)
    // Determine if shift is editable:
    // - For DSP users: always open in edit mode (fields will be disabled by validation in dialog)
    // - For other users: only planned and empty shifts can be edited
    if (isDSP) {
      setDetailsMode("edit")
    } else {
      setDetailsMode(
        shift.displayStatus === "planned" ||
          shift.displayStatus === "empty" ||
          shift.displayStatus === "open"
          ? "edit"
          : "view",
      )
    }
    setShiftDetailsOpen(true)
  }

  const handleShiftSave = async (updatedShift: ShiftDetails) => {
    const baselineShift = selectedShift ?? updatedShift
    const { personChanged, shouldCancelShift, hasChanges, onlyNotesChanged, updateBody } =
      buildShiftSaveDiff(updatedShift, baselineShift)
    const requiresAuditComment = baselineShift.type === "Internal" && !onlyNotesChanged

    if (!hasChanges) {
      toast.info("No changes to save")
      return
    }

    setIsSaving(true)
    Sentry.addBreadcrumb({
      category: "action",
      level: "info",
      message: "Attempt shift update",
      data: { shiftId: updatedShift.id },
    })
    return Sentry.startSpan(
      {
        op: "http.client",
        name: "Sheriff Update Shift",
      },
      async (span) => {
        span.setAttribute("shift_id", updatedShift.id)
        span.setAttribute("has_cancelled_reason", Boolean(updatedShift.cancelledReason))

        try {
          let peopleId: string | null = null
          if (personChanged && updatedShift.personId) {
            const { data: personData, error: personError } = await supabase
              .from("people")
              .select("id")
              .eq("person", updatedShift.personId)
              .maybeSingle()

            if (personError) {
              toast.error("Failed to find person")
              throw personError
            }

            if (personData) {
              peopleId = personData.id
            }
          }
          if (personChanged) {
            updateBody.peopleId = peopleId
          }

          // DSP users can only update person, and cannot cancel shifts
          if (isDSP) {
            if (!personChanged) {
              toast.info("No changes to save")
              return
            }

            await shiftsService.updateShift({
              id: updatedShift.id,
              peopleId: peopleId,
            })
            toast.success("Person assignment updated successfully")
          } else if (shouldCancelShift) {
            // If cancelledReason is provided, call cancel endpoint instead of update
            const cancelBody = await withCancellationAuditComment(
              {
                id: updatedShift.id,
                cancelledReason: updatedShift.cancelledReason,
              } as CancelShiftBody,
              { skipPrompt: !requiresAuditComment },
            )
            await shiftsService.cancelShift(cancelBody)
            toast.success("Block cancelled successfully")
          } else {
            const updateRequest = await withUpdateAuditComment(updateBody, {
              skipPrompt: !requiresAuditComment,
            })
            await shiftsService.updateShift(updateRequest)
            toast.success("Block updated successfully")
          }

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["shifts"] })

          // Only close dialog on successful save
          setShiftDetailsOpen(false)
          Sentry.addBreadcrumb({
            category: "action",
            level: "info",
            message: "Shift updated successfully",
            data: { shiftId: updatedShift.id },
          })
          if (sentryVerbose) {
            logger.info("Shift updated", {
              shiftId: updatedShift.id,
              cancelled: Boolean(updatedShift.cancelledReason),
            })
          }
        } catch (error: unknown) {
          console.error("Failed to update shift:", error)
          Sentry.captureException(error)
          if (sentryVerbose) {
            logger.error("Shift update failed", {
              shiftId: updatedShift.id,
              error,
            })
          }
          toast.error(getErrorMessage(error, "Failed to update block"))
          // Dialog remains open so user can fix the error and try again
        } finally {
          setIsSaving(false)
        }
      },
    )
  }

  const handleForceClockIn = async (shiftId: string) => {
    setIsSaving(true)
    Sentry.addBreadcrumb({
      category: "action",
      level: "info",
      message: "Attempt force clock in",
      data: { shiftId },
    })
    return Sentry.startSpan(
      {
        op: "http.client",
        name: "Sheriff Force Clock In",
      },
      async (span) => {
        span.setAttribute("shift_id", shiftId)
        try {
          await timesheetService.forceClockIn({ shiftId })
          toast.success("Successfully forced clock in")

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["shifts"] })

          // Close dialog
          setShiftDetailsOpen(false)
          Sentry.addBreadcrumb({
            category: "action",
            level: "info",
            message: "Force clock in successful",
            data: { shiftId },
          })
          if (sentryVerbose) {
            logger.info("Forced clock in", { shiftId })
          }
        } catch (error: unknown) {
          console.error("Failed to force clock in:", error)
          Sentry.captureException(error)
          if (sentryVerbose) {
            logger.error("Force clock in failed", { shiftId, error })
          }
          toast.error(getErrorMessage(error, "Failed to force clock in"))
        } finally {
          setIsSaving(false)
        }
      },
    )
  }

  const handleForceClockOut = async (timesheetId: string) => {
    setIsSaving(true)
    Sentry.addBreadcrumb({
      category: "action",
      level: "info",
      message: "Attempt force clock out",
      data: { timesheetId },
    })
    return Sentry.startSpan(
      {
        op: "http.client",
        name: "Sheriff Force Clock Out",
      },
      async (span) => {
        span.setAttribute("timesheet_id", timesheetId)
        try {
          await timesheetService.forceClockOut({ timesheetId })
          toast.success("Successfully forced clock out")

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["shifts"] })

          // Close dialog
          setShiftDetailsOpen(false)
          Sentry.addBreadcrumb({
            category: "action",
            level: "info",
            message: "Force clock out successful",
            data: { timesheetId },
          })
          if (sentryVerbose) {
            logger.info("Forced clock out", { timesheetId })
          }
        } catch (error: unknown) {
          console.error("Failed to force clock out:", error)
          Sentry.captureException(error)
          if (sentryVerbose) {
            logger.error("Force clock out failed", { timesheetId, error })
          }
          toast.error(getErrorMessage(error, "Failed to force clock out"))
        } finally {
          setIsSaving(false)
        }
      },
    )
  }

  return {
    // State
    shiftDetailsOpen,
    setShiftDetailsOpen,
    selectedShift,
    detailsMode,
    isSaving,
    // Handlers
    handleShiftClick,
    handleShiftSave,
    handleForceClockIn,
    handleForceClockOut,
  }
}
